import {
    OPERATOR_PLATFORM,
    TPM,
    VENDOR_DEFINED,
    statuses,
} from '../../constants';

export const CALLING_DISABLED = 'Calling Disabled';

/**
 * The pill options a service's own card passes: Value Add and Operator
 * Platform services get the Value Add card, and the Teams Phone Mobile card
 * flags the company's expired trial.
 *
 * @param {{ serviceType: string }} [service]
 * @param {{ isTrial?: boolean, trialEnd?: string }} [companyData]
 * @returns {{ isVendorCard: boolean, isTrialExpired: boolean }}
 */
export const getCardPillOptions = (service, companyData) => ({
    isVendorCard:
        service?.serviceType === VENDOR_DEFINED ||
        service?.serviceType === OPERATOR_PLATFORM,
    isTrialExpired:
        service?.serviceType === TPM &&
        Boolean(companyData?.isTrial) &&
        new Date(companyData?.trialEnd + 'Z') < new Date(),
});

/**
 * What a service card's status pill is drawn from: the colour (`value`) and
 * the label it passes to `statuses`. The pill and the collapsed header's
 * badge both read it, so the badge says what the card says.
 *
 * - A Value Add card reads its status' own text.
 * - An expired trial reads "Calling Disabled", in red.
 * - Any other card reads only its statusLabel (also its status when it has a
 *   tooltip icon), so with none, the colour picks the text ("Healthy").
 *
 * @param {object} [cardStatus]
 * @param {{ isVendorCard?: boolean, isTrialExpired?: boolean }} [options]
 * @returns {{ value?: string, label?: string }}
 */
export const getStatusPill = (
    cardStatus,
    { isVendorCard = false, isTrialExpired = false } = {},
) => {
    if (isVendorCard) {
        return {
            value: cardStatus?.color,
            label:
                cardStatus?.statusLabel ||
                cardStatus?.status ||
                cardStatus?.statusDisplay?.statusDisplayText,
        };
    }
    if (isTrialExpired) {
        return { value: 'red', label: CALLING_DISABLED };
    }
    return {
        value: cardStatus?.color,
        label: cardStatus?.tooltipIcon
            ? cardStatus?.statusLabel || cardStatus?.status
            : cardStatus?.statusLabel,
    };
};

/**
 * The text a service card's status pill shows. Empty when the pill is blank.
 *
 * @param {object} [cardStatus]
 * @param {{ isVendorCard?: boolean, isTrialExpired?: boolean }} [options]
 * @returns {string}
 */
const getServiceStatusLabel = (cardStatus, options) => {
    const { value, label } = getStatusPill(cardStatus, options);
    return statuses(label)[value]?.displayedName || '';
};

export default getServiceStatusLabel;
